import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  BRICK_COLOR_CATALOG,
  BRICK_PREVIEW_FONT_FAMILY,
  BRICK_SIZE_CONFIG,
  fitBrickText,
  getBrickColorOptions,
  millimetersToPixels,
  normalizeBrickText,
  pointsToPixels,
  TBrickSize,
  TOneOffEventFormValues
} from '../../../entities/events/oneOff';
import styles from './OneOffEventForm.module.scss';

export const BrickPreview = () => {
  const { control, setValue } = useFormContext<TOneOffEventFormValues>();
  const watchedBrickSize = useWatch({ control, name: 'brickSize' });
  const brickColor = useWatch({ control, name: 'brickColor' });
  const brickText = useWatch({ control, name: 'brickText' });

  const brickSize: TBrickSize = watchedBrickSize || '2x4';
  const config = BRICK_SIZE_CONFIG[brickSize];
  const availableColors = useMemo(() => getBrickColorOptions(brickSize), [brickSize]);

  useEffect(() => {
    if (availableColors.some(({ value }) => value === brickColor)) return;

    setValue('brickColor', availableColors[0]?.value || null, {
      shouldDirty: true,
      shouldValidate: true
    });
  }, [availableColors, brickColor, setValue]);

  const normalizedText = normalizeBrickText(brickText);
  const previewText = normalizedText || 'Your text';
  const fit = useMemo(() => fitBrickText(previewText, brickSize), [brickSize, previewText]);
  const color = BRICK_COLOR_CATALOG[brickColor || ''] || availableColors[0];
  const width = millimetersToPixels(config.widthMm);
  const height = millimetersToPixels(config.heightMm);
  const studCount = Number(brickSize.split('x')[1]);
  const studWidth = millimetersToPixels(4.8);
  const studHeight = millimetersToPixels(1.8);
  const characterCount = Array.from(normalizedText).length;
  const exceedsRecommendation = characterCount > config.recommendedCharacters;

  return (
    <section className={styles.preview} aria-live='polite'>
      <div className={styles.previewHeader}>
        <h2>Printed brick preview</h2>
        <span>{`${fit.fontSize} pt`}</span>
      </div>
      <div className={styles.previewCanvas}>
        <svg
          aria-label={`Preview of ${brickSize} ${color?.label || ''} brick with the text ${fit.normalizedText}`}
          className={styles.previewBrick}
          preserveAspectRatio='xMidYMid meet'
          role='img'
          viewBox={`0 ${-studHeight - 0.5} ${width} ${height + studHeight + 0.5}`}>
          {Array.from({ length: studCount }, (_, index) => (
            <rect key={index} fill={color?.background || '#f4f4f4'} height={studHeight + 1} rx='1' stroke='rgba(0, 0, 0, 0.28)' strokeWidth='1' width={studWidth} x={((index + 0.5) * width) / studCount - studWidth / 2} y={-studHeight} />
          ))}
          <rect fill={color?.background || '#f4f4f4'} height={height - 1} rx={Math.min(width, height) * 0.06} stroke='rgba(0, 0, 0, 0.28)' strokeWidth='1' width={width - 1} x='0.5' y='0.5' />
          <text dominantBaseline='central' fill={color?.text || '#111111'} fontFamily={BRICK_PREVIEW_FONT_FAMILY} fontSize={pointsToPixels(fit.fontSize)} fontWeight='300' textAnchor='middle' x={width / 2} xmlSpace='preserve' y={height / 2}>
            {fit.normalizedText}
          </text>
        </svg>
      </div>
      <p className={styles.previewHelp}>
        Tabs and special space characters are normalized and the text is centered.
        <br />
        Recommended maximum for a {brickSize} brick is {config.recommendedCharacters} characters.
      </p>
      {exceedsRecommendation && (
        <p className={styles.previewWarning} role='alert'>
          This text has {characterCount} characters and exceeds the recommended maximum. The font will be reduced to fit and, if the text still does not fit, it may overflow the printable area.
        </p>
      )}
      {!fit.fits && (
        <p className={styles.previewWarning} role='alert'>
          This text does not fit on one line at the minimum 6 pt size. Shorten it before creating the event.
        </p>
      )}
    </section>
  );
};
